"use client"
import { createContext, useContext, useReducer, useEffect } from "react"
import { sampleData } from "../data/sampleData"

const AppContext = createContext()

// Action types
const actionTypes = {
  LOGIN: "LOGIN",
  LOGOUT: "LOGOUT",
  SET_PATIENTS: "SET_PATIENTS",
  ADD_PATIENT: "ADD_PATIENT",
  UPDATE_PATIENT: "UPDATE_PATIENT",
  DELETE_PATIENT: "DELETE_PATIENT",
  SET_APPOINTMENTS: "SET_APPOINTMENTS",
  ADD_APPOINTMENT: "ADD_APPOINTMENT",
  UPDATE_APPOINTMENT: "UPDATE_APPOINTMENT",
  DELETE_APPOINTMENT: "DELETE_APPOINTMENT",
  SET_STAFF: "SET_STAFF",
  ADD_STAFF: "ADD_STAFF",
  UPDATE_STAFF: "UPDATE_STAFF",
  DELETE_STAFF: "DELETE_STAFF",
  SET_MEDICINES: "SET_MEDICINES",
  ADD_MEDICINE: "ADD_MEDICINE",
  UPDATE_MEDICINE: "UPDATE_MEDICINE",
  DELETE_MEDICINE: "DELETE_MEDICINE",
  SET_LAB_TESTS: "SET_LAB_TESTS",
  ADD_LAB_TEST: "ADD_LAB_TEST",
  UPDATE_LAB_TEST: "UPDATE_LAB_TEST",
  DELETE_LAB_TEST: "DELETE_LAB_TEST",
  SET_INVENTORY: "SET_INVENTORY",
  ADD_INVENTORY_ITEM: "ADD_INVENTORY_ITEM",
  UPDATE_INVENTORY_ITEM: "UPDATE_INVENTORY_ITEM",
  DELETE_INVENTORY_ITEM: "DELETE_INVENTORY_ITEM",
  SET_BILLS: "SET_BILLS",
  ADD_BILL: "ADD_BILL",
  UPDATE_BILL: "UPDATE_BILL",
  DELETE_BILL: "DELETE_BILL",
}

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  patients: [],
  appointments: [],
  staff: [],
  medicines: [],
  labTests: [],
  inventory: [],
  bills: [],
  loading: false,
  error: null,
}

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.LOGIN:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        error: null,
      }

    case actionTypes.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
      }

    case actionTypes.SET_PATIENTS:
      return {
        ...state,
        patients: action.payload,
      }

    case actionTypes.ADD_PATIENT:
      return {
        ...state,
        patients: [...state.patients, { ...action.payload, id: Date.now() }],
      }

    case actionTypes.UPDATE_PATIENT:
      return {
        ...state,
        patients: state.patients.map((patient) =>
          patient.id === action.payload.id ? { ...patient, ...action.payload } : patient,
        ),
      }

    case actionTypes.DELETE_PATIENT:
      return {
        ...state,
        patients: state.patients.filter((patient) => patient.id !== action.payload),
      }

    case actionTypes.SET_APPOINTMENTS:
      return {
        ...state,
        appointments: action.payload,
      }

    case actionTypes.ADD_APPOINTMENT:
      return {
        ...state,
        appointments: [...state.appointments, { ...action.payload, id: Date.now() }],
      }

    case actionTypes.UPDATE_APPOINTMENT:
      return {
        ...state,
        appointments: state.appointments.map((appointment) =>
          appointment.id === action.payload.id ? { ...appointment, ...action.payload } : appointment,
        ),
      }

    case actionTypes.DELETE_APPOINTMENT:
      return {
        ...state,
        appointments: state.appointments.filter((appointment) => appointment.id !== action.payload),
      }

    case actionTypes.SET_STAFF:
      return {
        ...state,
        staff: action.payload,
      }

    case actionTypes.ADD_STAFF:
      return {
        ...state,
        staff: [...state.staff, { ...action.payload, id: Date.now() }],
      }

    case actionTypes.UPDATE_STAFF:
      return {
        ...state,
        staff: state.staff.map((member) =>
          member.id === action.payload.id ? { ...member, ...action.payload } : member,
        ),
      }

    case actionTypes.DELETE_STAFF:
      return {
        ...state,
        staff: state.staff.filter((member) => member.id !== action.payload),
      }

    case actionTypes.SET_MEDICINES:
      return {
        ...state,
        medicines: action.payload,
      }

    case actionTypes.ADD_MEDICINE:
      return {
        ...state,
        medicines: [...state.medicines, { ...action.payload, id: Date.now() }],
      }

    case actionTypes.UPDATE_MEDICINE:
      return {
        ...state,
        medicines: state.medicines.map((medicine) =>
          medicine.id === action.payload.id ? { ...medicine, ...action.payload } : medicine,
        ),
      }

    case actionTypes.DELETE_MEDICINE:
      return {
        ...state,
        medicines: state.medicines.filter((medicine) => medicine.id !== action.payload),
      }

    case actionTypes.SET_LAB_TESTS:
      return {
        ...state,
        labTests: action.payload,
      }

    case actionTypes.ADD_LAB_TEST:
      return {
        ...state,
        labTests: [...state.labTests, { ...action.payload, id: Date.now() }],
      }

    case actionTypes.UPDATE_LAB_TEST:
      return {
        ...state,
        labTests: state.labTests.map((test) => (test.id === action.payload.id ? { ...test, ...action.payload } : test)),
      }

    case actionTypes.DELETE_LAB_TEST:
      return {
        ...state,
        labTests: state.labTests.filter((test) => test.id !== action.payload),
      }

    case actionTypes.SET_INVENTORY:
      return {
        ...state,
        inventory: action.payload,
      }

    case actionTypes.ADD_INVENTORY_ITEM:
      return {
        ...state,
        inventory: [...state.inventory, { ...action.payload, id: Date.now() }],
      }

    case actionTypes.UPDATE_INVENTORY_ITEM:
      return {
        ...state,
        inventory: state.inventory.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item,
        ),
      }

    case actionTypes.DELETE_INVENTORY_ITEM:
      return {
        ...state,
        inventory: state.inventory.filter((item) => item.id !== action.payload),
      }

    case actionTypes.SET_BILLS:
      return {
        ...state,
        bills: action.payload,
      }

    case actionTypes.ADD_BILL:
      return {
        ...state,
        bills: [...state.bills, { ...action.payload, id: Date.now() }],
      }

    case actionTypes.UPDATE_BILL:
      return {
        ...state,
        bills: state.bills.map((bill) => (bill.id === action.payload.id ? { ...bill, ...action.payload } : bill)),
      }

    case actionTypes.DELETE_BILL:
      return {
        ...state,
        bills: state.bills.filter((bill) => bill.id !== action.payload),
      }

    default:
      return state
  }
}

// Context Provider Component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState)

  // Load sample data on app start
  useEffect(() => {
    dispatch({ type: actionTypes.SET_PATIENTS, payload: sampleData.patients })
    dispatch({ type: actionTypes.SET_APPOINTMENTS, payload: sampleData.appointments })
    dispatch({ type: actionTypes.SET_STAFF, payload: sampleData.staff })
    dispatch({ type: actionTypes.SET_MEDICINES, payload: sampleData.medicines })
    dispatch({ type: actionTypes.SET_LAB_TESTS, payload: sampleData.labTests })
    dispatch({ type: actionTypes.SET_INVENTORY, payload: sampleData.inventory })
    dispatch({ type: actionTypes.SET_BILLS, payload: sampleData.bills })
  }, [])

  // Action creators
  const actions = {
    login: (userData) => dispatch({ type: actionTypes.LOGIN, payload: userData }),
    logout: () => dispatch({ type: actionTypes.LOGOUT }),

    // Patient actions
    addPatient: (patient) => dispatch({ type: actionTypes.ADD_PATIENT, payload: patient }),
    updatePatient: (patient) => dispatch({ type: actionTypes.UPDATE_PATIENT, payload: patient }),
    deletePatient: (id) => dispatch({ type: actionTypes.DELETE_PATIENT, payload: id }),

    // Appointment actions
    addAppointment: (appointment) => dispatch({ type: actionTypes.ADD_APPOINTMENT, payload: appointment }),
    updateAppointment: (appointment) => dispatch({ type: actionTypes.UPDATE_APPOINTMENT, payload: appointment }),
    deleteAppointment: (id) => dispatch({ type: actionTypes.DELETE_APPOINTMENT, payload: id }),

    // Staff actions
    addStaff: (staff) => dispatch({ type: actionTypes.ADD_STAFF, payload: staff }),
    updateStaff: (staff) => dispatch({ type: actionTypes.UPDATE_STAFF, payload: staff }),
    deleteStaff: (id) => dispatch({ type: actionTypes.DELETE_STAFF, payload: id }),

    // Medicine actions
    addMedicine: (medicine) => dispatch({ type: actionTypes.ADD_MEDICINE, payload: medicine }),
    updateMedicine: (medicine) => dispatch({ type: actionTypes.UPDATE_MEDICINE, payload: medicine }),
    deleteMedicine: (id) => dispatch({ type: actionTypes.DELETE_MEDICINE, payload: id }),

    // Lab test actions
    addLabTest: (test) => dispatch({ type: actionTypes.ADD_LAB_TEST, payload: test }),
    updateLabTest: (test) => dispatch({ type: actionTypes.UPDATE_LAB_TEST, payload: test }),
    deleteLabTest: (id) => dispatch({ type: actionTypes.DELETE_LAB_TEST, payload: id }),

    // Inventory actions
    addInventoryItem: (item) => dispatch({ type: actionTypes.ADD_INVENTORY_ITEM, payload: item }),
    updateInventoryItem: (item) => dispatch({ type: actionTypes.UPDATE_INVENTORY_ITEM, payload: item }),
    deleteInventoryItem: (id) => dispatch({ type: actionTypes.DELETE_INVENTORY_ITEM, payload: id }),

    // Bill actions
    addBill: (bill) => dispatch({ type: actionTypes.ADD_BILL, payload: bill }),
    updateBill: (bill) => dispatch({ type: actionTypes.UPDATE_BILL, payload: bill }),
    deleteBill: (id) => dispatch({ type: actionTypes.DELETE_BILL, payload: id }),
  }

  return <AppContext.Provider value={{ state, actions }}>{children}</AppContext.Provider>
}

// Custom hook to use the context
export const useApp = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error("useApp must be used within an AppProvider")
  }
  return context
}

export { actionTypes }
